import React, { useEffect, useRef, useCallback, FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import Moment from 'moment';
import { extendMoment } from 'moment-range';
import TimeIcon from '@bluevalet/react-icons/time';
import { InputSelect } from '@bluevalet/common-ui';

import { Api } from '@/modules/common/model';
import isEmpty from '@/utils/isEmpty';

import { getAvailableTimesFromSlots, getAvailableSlot, isErrorField } from '../utils';
import { setArrivalSlotAvailable, setArrivalSlotUnAvailable } from '../actions/slot';
import { State } from '../reducers';
import { TripDetail } from '../model';

const moment = extendMoment(Moment);

interface Props {
  prefix: 'start' | 'end';
  date?: Date;
  hour?: string;
  label: string | null | undefined;
  placeholder?: string;
  slots?: Api.SiteSlots;
  forceValidation?: boolean;
  notice?: string;
  isFetchingSlots: boolean;
  onHourChange: (value: string) => void;
  tripDetail: TripDetail | undefined;
}

const HourPicker: FC<Props> = ({
  prefix,
  date,
  hour,
  label,
  placeholder,
  slots,
  forceValidation,
  notice,
  isFetchingSlots,
  onHourChange,
  tripDetail,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const departure = useSelector((state: State) => state.booking.departure);
  const arrival = useSelector((state: State) => state.booking.arrival);
  const isHourForced = useSelector((state: State) => state.booking.isArrivalSlotAvailable);

  const prevTripDetailRef = useRef<TripDetail | undefined>();

  const isDeparture = prefix === 'start';
  const isArrival = prefix === 'end';

  const getDisplayedSlots = useCallback(() => {
    if (!slots) return [];

    const sameDateForDeparture = moment(arrival.date).isSame(date, 'day');
    const sameDateForArrival = moment(departure.date).isSame(date, 'day');
    let start = slots.startTime;
    let end = slots.endTime;

    if (isEmpty(tripDetail)) {
      if (isDeparture && sameDateForDeparture && arrival.hour) {
        end = moment(arrival.hour, 'HH:mm').format('HH:mm');
      }
      if (isArrival && sameDateForArrival && departure.hour) {
        start = moment(departure.hour, 'HH:mm').add(slots.step, 'minutes').format('HH:mm');
      }
    } else if (isDeparture) {
      end = moment(tripDetail.departureTime, 'DD/MM/YYYY HH:mm').subtract(slots.step, 'minutes').format('HH:mm');
    } else {
      start = moment(tripDetail.arrivalTime, 'DD/MM/YYYY HH:mm').subtract(slots.step, 'minutes').format('HH:mm');
    }

    const startMoment = moment(start, 'HH:mm');
    const endMoment = moment(end, 'HH:mm');

    return getAvailableTimesFromSlots(slots, startMoment, endMoment);
  }, [slots, date, tripDetail, departure, arrival, isDeparture, isArrival]);

  useEffect(() => {
    const prevTripDetail = prevTripDetailRef.current;

    if (isDeparture && !isEmpty(tripDetail) && !isHourForced) {
      if (hour !== undefined && hour.length > 0) {
        const selectedDate = moment(date);
        const [hours, minutes] = hour.split(':');
        selectedDate.set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10) });
        const departureTime = moment(tripDetail?.departureTime, 'DD/MM/YYYY HH:mm');
        if (selectedDate.isAfter(departureTime)) onHourChange('');
      }
    }

    if (isArrival && !isEmpty(tripDetail) && !isHourForced) {
      const displayedSlots = getDisplayedSlots();
      const findSlot = getAvailableSlot(tripDetail, displayedSlots);
      if (!isEmpty(findSlot) && !findSlot.isUnavailable) {
        if (hour === undefined) onHourChange(findSlot.value);
        dispatch(setArrivalSlotAvailable());
      }
      if (hour !== undefined && hour.length > 0) {
        const selectedDate = moment(date);
        const [hours, minutes] = hour.split(':');
        selectedDate.set({ hours: parseInt(hours, 10), minutes: parseInt(minutes, 10) });
        const arrivalTime = moment(tripDetail?.arrivalTime, 'DD/MM/YYYY HH:mm');
        if (
          selectedDate.isBefore(arrivalTime) ||
          (selectedDate.isAfter(arrivalTime) && prevTripDetail?.arrivalTime === undefined)
        ) {
          onHourChange('');
        }
      }
    }

    if (isArrival && tripDetail !== prevTripDetail) {
      const displayedSlots = getDisplayedSlots();
      const findSlot = tripDetail ? getAvailableSlot(tripDetail, displayedSlots) : undefined;
      if (!isEmpty(findSlot) && !findSlot.isUnavailable) onHourChange(findSlot.value);
      else dispatch(setArrivalSlotUnAvailable());
    }

    prevTripDetailRef.current = tripDetail;
  }, [date, hour, tripDetail, isHourForced, isDeparture, isArrival, onHourChange, getDisplayedSlots, dispatch]);

  const times = getDisplayedSlots();
  const unavailableHours = times.filter(({ isUnavailable }) => isUnavailable).map(({ value }) => value);
  const hourValue = hour ? (unavailableHours.indexOf(hour) === -1 ? hour : null) : null;

  const hourName = `${prefix}-time`;
  const fullLabel = label ? t(label) : null;
  const fullPlaceholder = t(placeholder || 'hour');

  return (
    <div className="HourPicker">
      {isFetchingSlots && (
        <InputSelect id="loading" name="loading" label={fullLabel} placeholder={fullPlaceholder} loading />
      )}

      {!isFetchingSlots && (
        <InputSelect
          data-cy={hourName}
          id={hourName}
          name={hourName}
          label={fullLabel}
          value={hourValue || ''}
          onChange={onHourChange}
          placeholder={fullPlaceholder}
          success={false}
          warning={false}
          error={isErrorField(forceValidation, hourValue)}
          message=""
          icon={<TimeIcon />}
          notice={notice ? { question: true, content: t(notice) } : null}>
          {times.map(time => (
            <option key={time.value} value={time.value} disabled={time.isUnavailable}>
              {time.isUnavailable ? `${time.value} (${t('full')})` : time.value}
            </option>
          ))}
        </InputSelect>
      )}
    </div>
  );
};

export default HourPicker;
